import { uuid } from '@sanity/uuid';
import { useState } from 'react';

import { AiThreadResponseQl } from '../../../../../../data/smart-search/hooks';
import { environment } from '../../../../../../environment.prod';
import { useLogService } from '../../../../../../hooks/useLogService';
import { useMarket } from '../../../../../../providers/MarketProvider';
import { useSembonusUser } from '../../../../../../providers/UserProvider';
import { SmartSearchService } from '../../../../../../services/SmartSearchService/SmartSearchService';
import { propertiesAsStrings } from '../../../../../../utils/helpers';
import { ItineraryMessage } from './components/Message/Message.types';

type Result = {
  isServerError: boolean;
  itinerary?: AiThreadResponseQl;
  plain?: string;
};

export const useApi = () => {
  const market = useMarket();
  const [threadId, setThreadId] = useState<string>();
  const [sessionId, setSessionId] = useState<string>();

  const sendMessage = async (text: string): Promise<Result> => {
    const messageId = uuid();

    try {
      const response = await SmartSearchService.getThreadAi(
        {
          text,
          messageId,
          threadId,
          sessionId,
          market: `https://${market.host}`,
        },
        environment.smartSearchApi
      );

      response.threadId && setThreadId(response.threadId);
      response.sessionId && setSessionId(response.sessionId);

      return {
        isServerError: false,
        itinerary: response?.recipeJson ? response : undefined,
        plain: response?.replyToUser ? response.replyToUser : undefined,
      };
    } catch (e) {
      return {
        isServerError: true,
      };
    }
  };

  return { sendMessage };
};

export const useAnalytics = () => {
  const { userInfo } = useSembonusUser();
  const { logEvent } = useLogService();

  const onInputInitiated = () => {
    logEvent('ai_input_text_initiated', undefined, {
      internalUser: userInfo?.member?.internalUser?.toString(),
      version: '2',
    });
  };

  const onItineraryReceived = (
    prompt: string,
    response: ItineraryMessage['content']
  ) => {
    logEvent('ai_trip_output_displayed', undefined, {
      prompt,
      internalUser: userInfo?.member?.internalUser?.toString(),
      userNameHash: userInfo?.member?.userNameHash,
      version: '2',
      ...propertiesAsStrings(response),
    });
  };

  const onViewTripClicked = (
    prompt: string,
    response: ItineraryMessage['content']
  ) => {
    logEvent('ai_view_trip', undefined, {
      prompt,
      internalUser: userInfo?.member?.internalUser?.toString(),
      userNameHash: userInfo?.member?.userNameHash,
      version: '2',
      ...propertiesAsStrings(response),
    });
  };

  return { onInputInitiated, onItineraryReceived, onViewTripClicked };
};
